import { Box, IconButton, InputAdornment, TextField, Typography, styled } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import {
  MUIDataTableColumn,
  MUIDataTableColumnDef,
  MUIDataTableCustomHeadRenderer,
  MUIDataTableMeta,
  MUISortOptions,
} from 'mui-datatables';
import { RewardType } from 'models/RewardType';
import CloseIcon from '@mui/icons-material/Close';
import { StyledRewardContainer, StyledStarIcon, StyledTableHeader } from 'components/StatisticsDataTable/StatisticsDataTable.styles';
import { LessonProgressStatus } from 'models/LessonProgressStatus';
import { DEFAULT_SCORE, getScoreFromCompareArg, getScoreFromRowData, isScore, makeScoreFilterLogic, makeSortScore } from 'components/StatisticsDataTable/Columns/ScoreColumn';
import { buildDateTimeRangePredicate, datePart, getDateFromCompareArg, isValidDateInput, isValidTimeInput, makeSortDateCompleted, maskDateInput, maskTimeInput } from 'components/StatisticsDataTable/Columns/DateCompletedColumn';
import { buildMinutesPredicate } from 'components/StatisticsDataTable/Columns/TimeSpentColumn';
import { columnIndex, getColumnWidth } from 'components/StatisticsDataTable/Columns/columnWidths';
import { Hms } from 'components/StatisticsDataTable/Dto/DataTableStatisticDto';
import { TruncatedCell } from 'components/StatisticsDataTable/TruncatedCell';
import { MakeSortableHead } from 'helpers/sortTableHeaderHelper';
import type { TFunction } from 'i18next';
import type { Theme } from '@mui/material';
import { isUncompleted } from 'helpers/lessonProgress';

const StyledFilterBox = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  paddingTop: theme.spacing(1),
}));

const StyledDateTimeGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(1.5),
}));

const StyledFieldGroup = styled('fieldset')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: Number(theme.shape.borderRadius),
  padding: theme.spacing(1.5),
  margin: 0,
  minWidth: 0,
}));

const StyledFieldGroupLegend = styled('legend')(({ theme }) => ({
  padding: `0 ${theme.spacing(0.75)}`,
  fontSize: '0.85rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
}));

const DATE_SAMPLE = '31/12/2026';
const TIME_SAMPLE = '23:59';

export type DateTimeRangeValues = [string, string, string, string];

interface DateTimeRangeFilterLabels {
  groupTitle: string;
  dateFrom: string;
  timeFrom: string;
  dateTo: string;
  timeTo: string;
}

interface DateTimeRangeFilterProps {
  value: DateTimeRangeValues;
  labels: DateTimeRangeFilterLabels;
  onApply: (value: DateTimeRangeValues) => void;
  t: TFunction;
}

const timeMissingDate = (dateRaw: string, timeRaw: string): boolean =>
  (timeRaw ?? '').trim() !== '' && (dateRaw ?? '').trim() === '';

const dateError = (date: string, t: TFunction): string =>
  isValidDateInput(date) ? '' : t('statistics.dateFilterInvalid');

const timeError = (date: string, time: string, t: TFunction): string => {
  if (!isValidTimeInput(time)) return t('statistics.timeFilterInvalid');
  return timeMissingDate(date, time) ? t('statistics.timeRequiresDate') : '';
};

const fieldErrors = (values: DateTimeRangeValues, t: TFunction): [string, string, string, string] => [
  dateError(values[0], t),
  timeError(values[0], values[1], t),
  dateError(values[2], t),
  timeError(values[2], values[3], t),
];

const DateTimeRangeFilter = ({ value, labels, onApply, t }: DateTimeRangeFilterProps) => {
  const [draft, setDraft] = useState<DateTimeRangeValues>(value);
  const appliedRef = useRef<DateTimeRangeValues>(value);

  useEffect(() => {
    if (value.join('|') !== appliedRef.current.join('|')) {
      setDraft(value);
      appliedRef.current = value;
    }
  }, [value]);

  const errors = fieldErrors(draft, t);

  const commit = (next: DateTimeRangeValues) => {
    const allValid = fieldErrors(next, t).every((e) => e === '');
    const applied: DateTimeRangeValues = allValid
      ? (next.map((v) => v.trim()) as DateTimeRangeValues)
      : ['', '', '', ''];
    appliedRef.current = applied;
    onApply(applied);
  };

  const handleChange = (position: 0 | 1 | 2 | 3, isTime: boolean): React.ChangeEventHandler<HTMLInputElement> => (e) => {
    const next = [...draft] as DateTimeRangeValues;
    next[position] = isTime ? maskTimeInput(e.target.value) : maskDateInput(e.target.value);
    setDraft(next);
    commit(next);
  };

  const handleClear = (position: 0 | 1 | 2 | 3) => {
    const next = [...draft] as DateTimeRangeValues;
    next[position] = '';
    setDraft(next);
    commit(next);
  };

  const fieldProps = (position: 0 | 1 | 2 | 3, label: string, isTime: boolean) => ({
    fullWidth: true,
    size: 'small' as const,
    variant: 'outlined' as const,
    label,
    placeholder: isTime ? TIME_SAMPLE : DATE_SAMPLE,
    value: draft[position],
    onChange: handleChange(position, isTime),
    error: errors[position] !== '',
    helperText: errors[position],
    InputProps: {
      inputProps: { inputMode: 'numeric' as const },
      endAdornment: draft[position] ? (
        <InputAdornment position="end">
          <IconButton
            size="small"
            aria-label={t('dataTable.reset')}
            onClick={() => handleClear(position)}
            edge="end"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      ) : undefined,
    },
  });

  return (
    <StyledFilterBox>
      <StyledFieldGroup>
        <StyledFieldGroupLegend>{labels.groupTitle}</StyledFieldGroupLegend>
        <StyledDateTimeGrid>
          <TextField {...fieldProps(0, labels.dateFrom, false)} />
          <TextField {...fieldProps(1, labels.timeFrom, true)} />
          <TextField {...fieldProps(2, labels.dateTo, false)} />
          <TextField {...fieldProps(3, labels.timeTo, true)} />
        </StyledDateTimeGrid>
      </StyledFieldGroup>
    </StyledFilterBox>
  );
};

const dateTimeRangeFilterOptions = (
  t: TFunction,
  labels: DateTimeRangeFilterLabels
) => ({
  fullWidth: false,
  logic: (cellValue: string | null, filters: unknown[]) => {
    const predicate = buildDateTimeRangePredicate({
      fromDate: (filters?.[0] as string) ?? '',
      fromTime: (filters?.[1] as string) ?? '',
      toDate: (filters?.[2] as string) ?? '',
      toTime: (filters?.[3] as string) ?? '',
    });
    if (!predicate) return false;
    return !predicate(cellValue);
  },
  display: (filterList: (string | string[])[][], onChange: any, index: number, column: any) => {
    const current = filterList[index] ?? [];
    const value: DateTimeRangeValues = [
      (current[0] as string) ?? '',
      (current[1] as string) ?? '',
      (current[2] as string) ?? '',
      (current[3] as string) ?? '',
    ];

    const handleApply = (next: DateTimeRangeValues) => {
      const nextFilterList = [...filterList];
      nextFilterList[index] = next;
      onChange(nextFilterList[index], index, column);
    };

    return (
      <DateTimeRangeFilter
        key={`datetime-range-${index}`}
        value={value}
        labels={labels}
        onApply={handleApply}
        t={t}
      />
    );
  },
});

const renderDateTimeRangeChip = (title: string) => (v: unknown): string => {
  const values = Array.isArray(v) ? v : [];
  const from = [String(values[0] ?? '').trim(), String(values[1] ?? '').trim()].filter(Boolean).join(' ');
  const to = [String(values[2] ?? '').trim(), String(values[3] ?? '').trim()].filter(Boolean).join(' ');
  if (!from && !to) return '';
  if (from && to) return `${title}: ${from} - ${to}`;
  return `${title}: ${from || to}`;
};

const getRewardIconColor = (rewardType: RewardType) => {
  switch (rewardType) {
    case RewardType.Gold:
      return 'yellow';
    case RewardType.Silver:
      return 'silver';
    case RewardType.Bronze:
      return 'brown';
    default:
      return 'none';
  }
};

const getLessonProgressStatusText = (status: LessonProgressStatus, t: TFunction) => {
  switch (status) {
    case LessonProgressStatus.NotStarted:
      return t('statistics.lessonProgressStatus.notStarted');
    case LessonProgressStatus.InProgress:
      return t('statistics.lessonProgressStatus.inProgress');
    case LessonProgressStatus.Canceled:
      return t('statistics.lessonProgressStatus.canceled');
    default:
      return '';
  }
};

const STATUS_COL_INDEX = columnIndex('lessonProgressStatus');

export const getStatusFromRowData = (rowData: unknown[]): LessonProgressStatus | null => {
  const raw = rowData?.[STATUS_COL_INDEX];
  return typeof raw === 'number' ? (raw as LessonProgressStatus) : null;
};

type TextAlign = 'left' | 'center';

const makeHeadRender = (textAlign: TextAlign, labelPaddingLeft?: string) => (
  columnMeta: MUIDataTableCustomHeadRenderer,
  handleToggleColumn: (columnIndex: number) => void,
  sortOrder: MUISortOptions
) => (
  <StyledTableHeader key={columnMeta.index} $textAlign={textAlign} onClick={() => handleToggleColumn(columnMeta.index)}>
    <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
      <Typography paddingLeft={labelPaddingLeft} textAlign={textAlign} fontWeight={'500'}>
        {columnMeta.label}
      </Typography>
    </MakeSortableHead>
  </StyledTableHeader>
);

const makeTextCellRender = (textAlign: TextAlign, ml?: string) => (value: string | null) => (
  <TruncatedCell value={value ?? ''} textAlign={textAlign} ml={ml} />
);

const fixedWidthStyle = (name: string): { width?: number; minWidth?: number; maxWidth?: number } => {
  const width = getColumnWidth(name);
  return width ? { width, minWidth: width, maxWidth: width } : {};
};

const makeTagColumn = (name: string, label: string): MUIDataTableColumn => ({
  name,
  label,
  options: {
    filter: true,
    sort: true,
    customBodyRender: makeTextCellRender('center'),
    customHeadRender: makeHeadRender('center'),
    setCellProps: () => ({ style: fixedWidthStyle(name) }),
  },
});

const makeCountColumn = (name: string, label: string): MUIDataTableColumn => ({
  name,
  label,
  options: {
    filter: false,
    sort: true,
    customBodyRender: (value: number | null) => <TruncatedCell value={String(value ?? 0)} textAlign="center" />,
    customHeadRender: makeHeadRender('center'),
    setCellProps: () => ({ style: fixedWidthStyle(name) }),
  },
});

interface DateColumnArgs {
  name: string;
  label: string;
  chipTitle: string;
  groupTitle: string;
  display?: boolean;
  t: TFunction;
}

const makeDateColumn = ({ name, label, chipTitle, groupTitle, display, t }: DateColumnArgs): MUIDataTableColumn => ({
  name,
  label,
  options: {
    filter: true,
    filterType: 'custom',
    sort: true,
    ...(display === false ? { display: false } : {}),
    sortCompare: makeSortDateCompleted(getDateFromCompareArg),
    customFilterListOptions: {
      render: renderDateTimeRangeChip(chipTitle),
    },
    filterOptions: dateTimeRangeFilterOptions(t, {
      groupTitle,
      dateFrom: t('statistics.filterDateFrom'),
      timeFrom: t('statistics.filterTimeFrom'),
      dateTo: t('statistics.filterDateTo'),
      timeTo: t('statistics.filterTimeTo'),
    }),
    customBodyRender: (value: string | null, tableMeta: MUIDataTableMeta) => {
      const status = getStatusFromRowData(tableMeta.rowData);
      const display = name !== 'dateStarted' && status !== null && isUncompleted(status)
        ? getLessonProgressStatusText(status, t)
        : name === 'dateStarted'
          ? datePart(value)
          : value ?? '';
      return <TruncatedCell value={display} textAlign="center" />;
    },
    customHeadRender: makeHeadRender('center'),
    setCellProps: () => ({ style: fixedWidthStyle(name) }),
  },
});

interface GetStatisticsColumnsArgs {
  t: TFunction;
  theme: Theme;
  revertedRewardDictionary: Record<string, RewardType>;
}

export const getStatisticsColumns = ({
  t,
  theme,
  revertedRewardDictionary,
}: GetStatisticsColumnsArgs): MUIDataTableColumnDef[] => {
  const scoreFilters = {
    noPoints: t('statistics.filters.noPoints'),
    below50: t('statistics.filters.below50'),
    above50: t('statistics.filters.above50'),
    max: t('statistics.filters.max'),
  };

  return [
    {
      name: 'userDisplayName',
      label: t('statistics.userName'),
      options: {
        filter: true,
        sort: true,
        customBodyRender: makeTextCellRender('left', '0.4rem'),
        customHeadRender: makeHeadRender('left', theme.spacing(1.2)),
        setCellProps: () => ({
          style: { paddingLeft: 0 },
        }),
      },
    },
    {
      name: 'lessonName',
      label: t('statistics.lessonName'),
      options: {
        filter: true,
        sort: true,
        customBodyRender: makeTextCellRender('center', '0.4rem'),
        customHeadRender: makeHeadRender('center'),
      },
    },
    makeTagColumn('userTagA', t('statistics.tagA')),
    makeTagColumn('userTagB', t('statistics.tagB')),
    {
      name: 'rewardType',
      label: t('statistics.rewardType'),
      options: {
        filter: true,
        sort: true,
        customHeadRender: makeHeadRender('center'),
        customBodyRender: (value: string) => {
          const noRewardValue = t('rewardType.none');
          const color = revertedRewardDictionary[value];

          return (
            <StyledRewardContainer>
              {value === noRewardValue
                ? <CloseIcon />
                : <StyledStarIcon $color={getRewardIconColor(color)} />}
            </StyledRewardContainer>
          );
        },
        setCellProps: () => ({ style: fixedWidthStyle('rewardType') }),
      },
    },
    {
      name: 'score',
      label: t('statistics.points'),
      options: {
        filter: true,
        filterType: 'dropdown',
        filterOptions: {
          names: [
            scoreFilters.noPoints,
            scoreFilters.below50,
            scoreFilters.above50,
            scoreFilters.max,
          ],
          logic: makeScoreFilterLogic(scoreFilters, getScoreFromRowData),
          customFilterListOptions: {
            render: (v: string[]) => v,
          },
        },
        sortCompare: makeSortScore(getScoreFromCompareArg),
        customBodyRender: (value: any) => {
          const s = isScore(value) ? value : DEFAULT_SCORE;
          return <Typography textAlign={'center'}>{`${s.scoredPoints}/${s.maxPoints}`}</Typography>;
        },
        customHeadRender: makeHeadRender('center'),
        setCellProps: () => ({ style: fixedWidthStyle('score') }),
      },
    },
    makeDateColumn({
      name: 'dateCompleted',
      label: t('statistics.dateCompleted'),
      chipTitle: t('statistics.dateCompleted'),
      groupTitle: t('statistics.dateCompletedGroup'),
      display: false,
      t,
    }),
    makeDateColumn({
      name: 'dateStarted',
      label: t('statistics.dateStarted'),
      chipTitle: t('statistics.dateStarted'),
      groupTitle: t('statistics.dateStartedGroup'),
      t,
    }),
    {
      name: 'timeStarted',
      label: t('statistics.sessionStartTime'),
      options: {
        filter: true,
        sort: true,
        customBodyRender: makeTextCellRender('center'),
        customHeadRender: makeHeadRender('center'),
        setCellProps: () => ({ style: fixedWidthStyle('timeStarted') }),
      },
    },
    {
      name: 'timeSpent',
      label: t('statistics.timeSpent'),
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: makeTextCellRender('center'),
        customHeadRender: makeHeadRender('center'),
        setCellProps: () => ({ style: fixedWidthStyle('timeSpent') }),
      },
    },
    {
      name: 'convertedTimeSpent',
      label: 'Time spent (min)',
      options: {
        display: 'excluded',
        sort: false,
        filter: true,
        filterType: 'custom',
        customFilterListOptions: {
          render: (v: unknown) => {
            const first = Array.isArray(v) ? v[0] : v;
            const s = String(first ?? '').trim();
            return s ? `${t('statistics.timeSpent')}: ${s}` : '';
          },
        },
        filterOptions: {
          fullWidth: false,
          logic: (cellValue: Hms | null, filters: unknown[]) => {
            const raw = (filters?.[0] as string) ?? '';
            const pred = buildMinutesPredicate(raw);
            if (!pred) return false;
            return !pred(cellValue);
          },
          display: (filterList, onChange, index, column) => {
            const val: string = (filterList[index]?.[0] as string) ?? '';
            const normalized = val.replace(/\s+/g, '');
            const isValid = val.trim() === '' || /^[<>]\d+(?:\.\d+)?$/.test(normalized);

            const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
              const next = e.target.value;
              const nextFilterList = [...filterList];
              nextFilterList[index] = [next];
              onChange(nextFilterList[index], index, column);
            };

            return (
              <StyledFilterBox>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={t('statistics.filterMinutesLabel')}
                  placeholder="> 10 / < 10"
                  value={val}
                  onChange={handleChange}
                  error={val.trim() !== '' && !isValid}
                  helperText={t('statistics.filterMinutesHelp')}
                  InputProps={{ inputProps: { inputMode: 'text' } }}
                />
              </StyledFilterBox>
            );
          },
        },
      },
    },
    {
      name: 'lessonProgressStatus',
      label: t('statistics.status'),
      options: {
        filter: true,
        sort: true,
        filterType: 'dropdown',
        filterOptions: {
          names: [t('statistics.completed'), t('statistics.notCompleted')],
          logic: (value: unknown, filters: string[]) => {
            if (!filters?.length) return false;
            const label = typeof value === 'number'
              ? (isUncompleted(value as LessonProgressStatus) ? t('statistics.notCompleted') : t('statistics.completed'))
              : String(value);
            return !filters.includes(label);
          },
        },
        customBodyRender: (value: LessonProgressStatus) => (
          <TruncatedCell
            value={isUncompleted(value) ? t('statistics.notCompleted') : t('statistics.completed')}
            textAlign="center"
          />
        ),
        customHeadRender: makeHeadRender('center'),
        setCellProps: () => ({ style: fixedWidthStyle('lessonProgressStatus') }),
      },
    },
    {
      name: 'passed',
      label: t('statistics.passStatus'),
      options: {
        filter: true,
        sort: true,
        filterType: 'dropdown',
        filterOptions: {
          names: [t('general.yes'), t('general.no')],
          logic: (value: unknown, filters: string[]) => {
            if (!filters?.length) return false;
            const isPassed = value === true || value === t('general.yes');
            const label = isPassed ? t('general.yes') : t('general.no');
            return !filters.includes(label);
          },
        },
        customBodyRender: (value: boolean) => (
          <TruncatedCell value={value ? t('general.yes') : t('general.no')} textAlign="center" />
        ),
        customHeadRender: makeHeadRender('center'),
        setCellProps: () => ({ style: fixedWidthStyle('passed') }),
      },
    },
    makeCountColumn('completedAttemptsCount', t('statistics.completedAttempts')),
    makeCountColumn('totalAttemptsCount', t('statistics.totalAttempts')),
  ] as MUIDataTableColumnDef[];
};
